import React, { useState } from 'react';
import { QuestionFull as Neutral, X } from '@fabric-ui/icons';
import { ErrorFull as Error, SuccessFull as Success, WarningFullOrange as Warning } from '@fabric-ui/icons/colorful';
import { flex } from '@fabric-ui/theme';
import { Box, Collapse, IconButton, SxProps, useTheme } from '@mui/material';
import { ExpandCard } from './ExpandCard';

export interface Props {
  variant: AttentionBoxVariant;
  content: string | React.ReactElement;
  collapseContent?: string | React.ReactElement;
  showIcon?: boolean;
  small?: boolean;
  onClick?: () => void;
  onDismiss?: () => void;
  icon?: React.JSX.Element;
  sx?: SxProps;
}

export const AvAttentionBox: React.FC<Props> = ({
  variant,
  content,
  showIcon = true,
  small = false,
  onClick,
  collapseContent,
  onDismiss,
  icon,
  sx,
}) => {
  const [open, setOpen] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const bgVariant = {
    success: 'var(--color-surface-status-success-primary)',
    error: 'var(--color-surface-status-danger-primary)',
    neutral: 'var(--color-surface-status-neutral-primary)',
    warning: 'var(--color-surface-status-warning-primary)',
  };

  const iconVariant = {
    success: <Success />,
    error: <Error />,
    neutral: <Neutral />,
    warning: <Warning />,
  };

  return isDismissed ? null : (
    <Box
      aria-label="attention-box"
      onClick={() => {
        onClick?.();
      }}
      sx={{
        cursor: onClick ? 'pointer' : 'inherit',
        py: small ? 1 : '10px',
        px: 2,
        background: bgVariant[variant],
        ...(flex.col as any),
        borderRadius: 2,
        ...sx,
      }}>
      <Box sx={{ ...flex.itemsStart, gap: 1 }}>
        {showIcon && (icon || iconVariant[variant])}
        <Box sx={{ flex: 1 }}>{content}</Box>
        {collapseContent && <ExpandCard expand={open} onClick={() => setOpen(!open)} />}
        {onDismiss && (
          <IconButton
            onClick={() => {
              onDismiss();
              setIsDismissed(true);
            }}>
            <X />
          </IconButton>
        )}
      </Box>
      {collapseContent ? <Collapse in={open}>{collapseContent}</Collapse> : null}
    </Box>
  );
};

export enum AttentionBoxVariant {
  Warning = 'warning',
  Error = 'error',
  Success = 'success',
  Neutral = 'neutral',
}
